import type { Asset, DemoDoc, Layout, Step } from '@mado/schema';
import {
  CUE_FADE_MS,
  DRIFT_MIN_MS,
  readingMs,
  cuePlanDesktop,
  cuePlanStory,
  phonePlan,
  guideBeats,
  guidedStep,
  stepView,
  stepRest,
  scrollLayout,
  scrollStoryBeats,
  scrollStoryLayout,
  scrollStoryRange,
  overviewFrame,
  productRadiusPx,
  stepCamera,
  storyBeats,
  type ScrollStoryInput,
  type Motion,
  type PxRect,
  type CuePlan,
  type StoryBeat,
} from '@mado/schema/camera';
import type { Cue, NormRect } from '@mado/schema';
import { buildPicture } from './media';
import { placeLabel } from './cue-label';
import { EASE, dur, reflow } from './motion';
import { drawnWidth, stepLayouts } from './sizing';

/** A ring's words (`Stage.addLabel`): the text, where it may go and what it must not cover, in viewport px. */
export interface CueLabel {
  text: string;
  area: { x: number; y: number; w: number; h: number };
  avoid: { x: number; y: number; w: number; h: number }[];
}

/**
 * One step's pixels.
 *
 * The stage owns exactly two transforms and nothing else:
 *   .mado-layer  — transition movement between steps
 *   .mado-stage  — layout (focus-region scale + pan) within a step
 *
 * Both are pure `transform`, so every visual change is GPU-composited and the
 * browser never relayouts (CLAUDE.md / DESIGN-SYSTEM §5 rule 1).
 *
 * The camera (`@mado/schema/camera`) decides the layout a step rests at; the
 * runtime choreographs how the stage travels there (`setCamera`), and the
 * `transform` is still the only thing that moves.
 */
export class Stage {
  readonly layer: HTMLElement;
  private readonly stage: HTMLElement;
  private stickyTop: HTMLElement | null = null;
  private stickyBottom: HTMLElement | null = null;
  private layout: Layout | null = null;
  /** The product's corner radius in px of the picture: a mark's hole and ring take it. */
  private readonly corner: number;
  /** The cue mark on show (`showCue`). */
  private cueNode: HTMLElement | null = null;
  /** Each cue's ring box, in asset px: where its label goes beside it (`addLabel`). */
  private readonly holes = new WeakMap<
    HTMLElement,
    { x: number; y: number; w: number; h: number }
  >();
  private scrollP = 0;
  /** Phone story, scroll step: where the viewer is on the page. */
  private rail: { track: HTMLElement; thumb: HTMLElement } | null = null;
  private railKey = '';
  /** Phone story, scroll step: the viewer's sideways pan from the column, in px (clamped). */
  private panPx = 0;
  private readonly motion: Motion | undefined;
  /** What a whole-screen view shows: the page's content box (`overviewFrame`). */
  private readonly frame: NormRect;
  /**
   * Phone story: the step's walk through its shots inside `picture` (see
   * story.ts). Null when no story is told — every other case, as before.
   */
  private beatList: StoryBeat[] | null = null;
  /** The CSS width the <picture> is asked for (`sizes`); only ever grows. */
  private sizes = 0;

  readonly asset: Asset;
  readonly isScroll: boolean;
  /** The demo's first step: a story opens on the whole screen before it zooms in. */
  private readonly opening: boolean;

  constructor(
    doc: DemoDoc,
    /** The step as told here: with its phone track planned for the story's window (`relayout`). */
    private plan: Step,
    private readonly viewport: { w: number; h: number },
    /** Native phone capture on a phone is already 1:1 — focusing would only harm. */
    private readonly allowFocus: boolean,
    /** Origin to resolve root-relative `/a/...` asset URLs against. See media.ts. */
    private readonly assetBase?: string | null,
    /** Phone story: the picture area this step is framed in. Null: the whole viewport. */
    private picture: PxRect | null = null,
    /** Phone story, scroll step: open on the whole page before the column (not straight on from a scroll). */
    private readonly establish = true,
    /**
     * `sizes` for the picture: sizing.ts `stepSizes`, the widest this step
     * (or a step it can pan from) ever draws it. Absent: this step's own
     * framings, computed here.
     */
    sizes?: number,
    /**
     * Phone walkthrough: the demo's one close-up zoom (`walkZoom`). With it,
     * a step opens straight on its target at that zoom (the camera pans to
     * it from the step before); only the demo's opening step walks in from
     * the whole screen.
     */
    private zoom?: number,
    /**
     * Phone walkthrough: the step arrives on a new screen (`sameScreen`), so
     * it opens on the whole screen and holds before it moves to its target
     * (docs/plans/phone-overview.md). False: the same screen, the camera
     * carries on from where it is.
     */
    private readonly newScreen = false,
    /**
     * Desktop: where the step before left the camera on this same screen (`desktopEntry`); the
     * step's walk carries on from there instead of showing the screen whole again.
     */
    private entry: Layout | null = null,
  ) {
    const step = plan;
    this.isScroll = !!step.scroll;
    this.opening = doc.start === step.id;
    this.motion = doc.theme.motion;
    this.frame = overviewFrame(doc);
    const assetId = step.scroll ? step.scroll.assetTall : step.asset;
    this.asset = doc.assets[assetId]!;
    this.corner = productRadiusPx(doc.theme, this.asset);

    this.layer = el('div', 'mado-layer');
    this.stage = el('div', 'mado-stage');
    this.sizes = sizes ?? this.ownSizes();
    this.stage.appendChild(buildPicture(this.asset, this.sizes, this.assetBase));
    this.layer.appendChild(this.stage);

    if (step.scroll && (step.scroll.sticky.top > 0 || step.scroll.sticky.bottom > 0)) {
      this.buildSticky(step.scroll.sticky, this.sizes);
    }

    this.relayout();
  }

  /**
   * Sticky bands recovered by the pipeline's scroll stitcher — the nav bar and
   * tab bar that did NOT move while the content scrolled. Re-overlaying them is
   * what makes a stitched scroll read as a real app rather than a long picture.
   * `sizes` is the width the picture is drawn at, so the band picks the same
   * variant as the picture it must line up with pixel for pixel.
   */
  private buildSticky(sticky: { top: number; bottom: number }, sizes: number): void {
    if (sticky.top > 0) {
      const band = el('div', 'mado-sticky mado-sticky--top');
      band.appendChild(buildPicture(this.asset, sizes, this.assetBase));
      this.stickyTop = band;
      this.layer.appendChild(band);
    }
    if (sticky.bottom > 0) {
      const band = el('div', 'mado-sticky mado-sticky--bottom');
      band.appendChild(buildPicture(this.asset, sizes, this.assetBase));
      this.stickyBottom = band;
      this.layer.appendChild(band);
    }
  }

  /** The step on this stage (the same step, its phone track replanned on a resize). */
  get step(): Step {
    return this.plan;
  }

  /**
   * Recompute geometry for the current viewport. Called on mount and on
   * resize; `sizes` is the step's `stepSizes` under the new viewport. `replan`: the same step
   * planned for the new window (its phone track, `Step.phone`) and the demo's zoom there — a
   * track planned for the old window, or for none, would frame the wrong part.
   */
  relayout(
    viewport?: { w: number; h: number },
    picture?: PxRect | null,
    sizes?: number,
    replan?: { step: Step; zoom: number | undefined },
  ): void {
    if (viewport) Object.assign(this.viewport, viewport);
    if (picture !== undefined) this.picture = picture;
    if (replan && replan.step.id === this.plan.id) {
      this.plan = replan.step;
      this.zoom = replan.zoom;
    }
    // The entry framing was measured on the old viewport: a resize opens the walk whole.
    if (viewport || picture !== undefined) this.entry = null;

    this.layout = this.computeLayout();
    if (viewport) this.applySizes(sizes ?? this.ownSizes());
    this.applyTransform(false);
    this.applySticky();
    this.syncRail();
  }

  /**
   * A thin track inside the window's right edge with a thumb at the viewer's
   * place on the page, so a long page reads as scrolling and not as a tall
   * picture. Only the thumb moves, by transform. Not drawn when the whole
   * page already fits the window.
   */
  private syncRail(): void {
    const p = this.picture;
    const total = this.layout ? this.asset.h * this.layout.scale : 0;
    if (!this.isScroll || !p || total <= p.h * 1.02) {
      if (this.rail) this.rail.track.style.display = 'none';
      this.railKey = '';
      return;
    }
    if (!this.rail) {
      const track = el('div', 'mado-scroll-rail');
      const thumb = el('div', 'mado-scroll-thumb');
      track.appendChild(thumb);
      this.layer.appendChild(track);
      this.rail = { track, thumb };
    }
    const { track, thumb } = this.rail;
    const trackH = Math.max(1, p.h - 20);
    const thumbH = Math.max(24, Math.min(trackH, trackH * (p.h / total)));
    // Geometry only when it changed: these are layout writes, and this runs
    // on every frame of a drag. The thumb's transform is the only per-frame write.
    const key = `${p.x}|${p.y}|${p.w}|${trackH}|${thumbH}`;
    if (this.railKey !== key) {
      this.railKey = key;
      track.style.display = '';
      track.style.left = `${p.x + p.w - 7}px`;
      track.style.top = `${p.y + 10}px`;
      track.style.height = `${trackH}px`;
      thumb.style.height = `${thumbH}px`;
    }
    thumb.style.transform = `translate3d(0, ${(trackH - thumbH) * this.scrollP}px, 0)`;
  }

  private computeLayout(): Layout {
    if (this.isScroll) {
      // Phone story: the scroll walks the page's column at legible zoom
      // (`scrollStoryLayout`), after the whole page when it is arrived at.
      this.beatList = this.picture ? scrollStoryBeats(this.storyInput()!, this.establish) : null;
      return this.scrollAt();
    }
    // Narration cues (docs/plans/desktop-guided-camera.md §cues): the camera
    // follows the line's phrases; the walk is timed by the runtime
    // (`cuePlanFor`), here only where it starts and rests.
    if (this.cued) {
      const beats = this.cuePlanFor(null).beats;
      this.beatList =
        beats.length > 1
          ? beats.map((b, i) => ({
              reason: b.reason === 'target' ? ('target' as const) : ('establish' as const),
              layout: b.layout,
              holdMs: i < beats.length - 1 ? beats[i + 1]!.atMs - b.atMs : 0,
            }))
          : null;
      return beats[beats.length - 1]!.layout;
    }
    this.beatList =
      this.picture && !this.isScroll
        ? storyBeats(this.step, this.picture, this.asset, this.motion, {
            overview: this.opening,
            frame: this.frame,
            ...(this.zoom
              ? { zoom: this.zoom, lead: this.opening, establish: this.newScreen }
              : {}),
          })
        : null;
    if (this.beatList) return this.beatList[this.beatList.length - 1]!.layout;
    // Desktop guided camera (docs/plans/desktop-guided-camera.md): a step
    // whose document says what it is about walks the whole screen and its
    // regions before it rests. An action step rests where it always did.
    if (!this.picture && guidedStep(this.step, this.motion)) {
      const beats = guideBeats(
        this.step,
        {
          viewport: this.viewport,
          asset: this.asset,
          allowFocus: this.allowFocus,
          motion: this.motion,
        },
        { establish: this.opening || this.newScreen },
      );
      if (beats.length > 1) this.beatList = beats;
      return beats[beats.length - 1]!.layout;
    }
    return this.isScroll
      ? this.scrollAt()
      : stepCamera(this.step, {
          viewport: this.viewport,
          asset: this.asset,
          allowFocus: this.allowFocus,
          motion: this.motion,
        });
  }

  /**
   * Where a camera move pulls back to or starts from: the step's static
   * framing — the whole screen where it fits, the legibility zoom on a phone.
   * A scroll step has no camera; its layout is its rest.
   */
  restLayout(): Layout {
    if (this.beatList) return this.beatList[0]!.layout;
    return this.isScroll
      ? this.scrollAt()
      : stepRest(this.step, {
          viewport: this.viewport,
          asset: this.asset,
          allowFocus: this.allowFocus,
        });
  }

  /**
   * Phone walkthrough: the step's close-up on its target, even when the step
   * rests on the whole screen (an overview step) — where a spread goes.
   */
  closeLayout(): Layout {
    if (!this.picture || this.isScroll) return this.targetLayout();
    const beats = storyBeats(this.step, this.picture, this.asset, this.motion, {
      lead: false,
      view: 'action',
      ...(this.zoom ? { zoom: this.zoom } : {}),
    });
    return beats[beats.length - 1]!.layout;
  }

  /** The step follows its narration's cues (a cinematic step on a desktop, or any phone story step). */
  get cued(): boolean {
    if (this.isScroll) return false;
    // A showcase plays on the step's clock like cues (runtime `playShowcase`).
    if (this.step.showcase) return true;
    // A planned phone track (`Step.phone`) is played on the narration's clock, like cues.
    if (this.picture && this.step.phone) return true;
    // Cues, or a long line about the page as a whole: it drifts (`CuePlanInput.drift`).
    const text = this.step.narration?.text.trim();
    const drifts =
      !!this.picture && this.step.view === 'overview' && !!text && readingMs(text) >= DRIFT_MIN_MS;
    if (!this.step.cues?.length && !drifts) return false;
    return !!this.picture || guidedStep(this.step, this.motion);
  }

  /** The cue walk on the narration's clock (`narrationMs`; null: reading pace). */
  cuePlanFor(narrationMs: number | null): CuePlan {
    // The phone track planned at publish (docs/plans/phone-track.md); absent: the live camera.
    if (this.picture && this.step.phone) {
      return phonePlan(
        this.step as Step & { phone: NonNullable<Step['phone']> },
        this.picture,
        this.asset,
        narrationMs,
        this.viewport,
      );
    }
    if (this.picture) {
      const story = storyBeats(this.step, this.picture, this.asset, this.motion, {
        ...(this.zoom ? { zoom: this.zoom } : {}),
        lead: false,
        frame: this.frame,
      });
      const target = stepView(this.step) === 'overview' ? null : story[story.length - 1]!.layout;
      return cuePlanStory(
        this.step,
        this.picture,
        this.asset,
        target,
        narrationMs,
        this.viewport,
        this.frame,
      );
    }
    return cuePlanDesktop(
      this.step,
      {
        viewport: this.viewport,
        asset: this.asset,
        allowFocus: this.allowFocus,
        motion: this.motion,
      },
      narrationMs,
      this.entry,
    );
  }

  /**
   * Light a cue's rect on the picture: drawn inside the stage, in the asset's
   * pixels, so it rides every camera move. `highlight` and `zoom`: the rest
   * dimmed, a ring round the rect (a `look` at the tap target is not drawn: the
   * Watch cursor points at it, `Watch.point`; any other `look` comes here as a
   * highlight, `cueMark`). One cue at a time: a new one takes over from the
   * last (a skip or a pause can leave the last one's hide unrun). Fades in and
   * out (styles.ts `.mado-cue`). Returns the element, to hide later.
   *
   * `ring: false`: the dim only, its hole already on the rect, the ring to come (`lightCue`).
   * A ring that follows another within `CUE_DIM_HOLD_MS` takes the dim over at once, so the
   * page never comes back to full brightness between the two (runtime.ts `playCues`).
   */
  showCue(cue: Cue, dim: string, ring = true, label: CueLabel | null = null): HTMLElement {
    if (this.cueNode) this.hideCue(this.cueNode);
    const k = 1 / Math.max(1e-6, this.layout?.scale ?? 1);
    const a = this.asset;
    const r = cue.rect;
    const node = el('div', 'mado-cue');
    const st = node.style;
    // The dim is plain rectangles around the target and the ring a
    // target-sized box; the dim fades by colour, not opacity (styles.ts
    // `.mado-cue-d`), so no box gets a layer of its own. It was one
    // box-shadow spread 2x the picture with a blur, which killed the tab on
    // an iPhone (a WebContent memory kill); picture-sized and larger boxes,
    // and a clip box around them, still crashed it. On a phone the dim
    // therefore covers the picture only; the window around it stays as is.
    // Desktop reaches one picture past each edge, for the letterbox.
    st.left = st.top = '0';
    // The ring stays inside the picture: a rect at the screen's edge
    // would put it past the window's edge, cut. Its air is small: lines of
    // text sit a few px apart, and a wider ring cut through the next one.
    // `edge` keeps the whole ring line (2.5px) and its rounded corner inside, with air: at 3.5
    // the ring of a button flush with the screen's edge read as cut (Talentika s3, Add block).
    const pad = 3 * k;
    const edge = 7 * k;
    const hx = Math.max(edge, r.x * a.w - pad);
    const hy = Math.max(edge, r.y * a.h - pad);
    const hw = Math.min(a.w - edge, (r.x + r.w) * a.w + pad) - hx;
    const hh = Math.min(a.h - edge, (r.y + r.h) * a.h + pad) - hy;
    const mx = this.picture ? 0 : a.w;
    const my = this.picture ? 0 : a.h;
    const x0 = -mx;
    const y0 = -my;
    const x1 = a.w + mx;
    const y1 = a.h + my;
    const box = (l: number, t: number, w: number, h: number, css: string, cls: string): void => {
      if (w <= 0 || h <= 0) return;
      const d = el('div', cls);
      d.style.cssText = `position:absolute;left:${l}px;top:${t}px;width:${w}px;height:${h}px;${css}`;
      node.appendChild(d);
    };
    st.setProperty('--cue-dim', dim);
    const top = Math.max(y0, hy);
    const bot = Math.min(y1, hy + hh);
    const cl = Math.max(x0, hx);
    const cr = Math.min(x1, hx + hw);
    if (this.picture) {
      box(x0, y0, x1 - x0, top - y0, '', 'mado-cue-d');
      box(x0, bot, x1 - x0, y1 - bot, '', 'mado-cue-d');
      box(x0, top, cl - x0, bot - top, '', 'mado-cue-d');
      box(cr, top, x1 - cr, bot - top, '', 'mado-cue-d');
    } else {
      // Desktop: one box whose borders are the dim. Four boxes meeting at a fractional pixel
      // left a light seam along the mark's row, the whole width of the screen (Talentika s9,
      // 2026-10-13). Same painted area as the four; the phone keeps them (iOS rule above).
      box(
        x0,
        y0,
        x1 - x0,
        y1 - y0,
        `box-sizing:border-box;border-style:solid;border-width:${top - y0}px ${x1 - cr}px ${y1 - bot}px ${cl - x0}px;`,
        'mado-cue-b',
      );
    }
    // The hole has the product's own corners (square for a square product), and the ring with it.
    const rad = Math.min(this.corner + pad, hw / 2, hh / 2);
    if (rad >= 1) {
      // Each corner of the hole: a small clip with a ring whose inside is the hole's corner, its
      // band the dim (small boxes, the iOS rule above).
      for (const [cx, cy] of [
        [0, 0],
        [1, 0],
        [0, 1],
        [1, 1],
      ] as const) {
        const c = el('div', '');
        c.style.cssText = `position:absolute;overflow:hidden;left:${hx + cx * (hw - rad)}px;top:${hy + cy * (hh - rad)}px;width:${rad}px;height:${rad}px`;
        const d = el('div', 'mado-cue-b');
        d.style.cssText = `position:absolute;box-sizing:border-box;border-radius:50%;border-style:solid;border-width:${rad}px;width:${4 * rad}px;height:${4 * rad}px;left:${-(1 + cx) * rad}px;top:${-(1 + cy) * rad}px`;
        c.appendChild(d);
        node.appendChild(c);
      }
    }
    box(
      hx,
      hy,
      hw,
      hh,
      `border-radius:${rad}px;box-shadow:0 0 0 ${2.5 * k}px var(--mado-accent, #C9791A);`,
      'mado-cue-r',
    );
    if (!ring) node.classList.add('mado-cue--wait');
    this.holes.set(node, { x: hx, y: hy, w: hw, h: hh });
    this.stage.appendChild(node);
    if (ring && label) this.addLabel(node, label);
    reflow(node);
    node.classList.add('mado-cue--in');
    this.cueNode = node;
    return node;
  }

  /** Light the ring of a cue shown with its dim only (`showCue(…, false)`), with its label. */
  lightCue(node: HTMLElement, label: CueLabel | null = null): void {
    if (label) {
      this.addLabel(node, label);
      reflow(node);
    }
    node.classList.remove('mado-cue--wait');
  }

  /**
   * The ring's words beside it (cue-label.ts): in the stage with the ring, so it rides every
   * camera move, sized by the inverse of the zoom like the ring's line, so it reads at the same
   * size on screen at any zoom. The stage carries no `will-change`, so at rest its text is drawn
   * at the scale it is shown at (crisp). A small box fading by opacity, like the ring: no big
   * layer (the iOS rule above). Placed where the camera is going (`setCamera` names it first),
   * off the tooltip, inside the picture and the area the runtime gives.
   */
  private addLabel(node: HTMLElement, label: CueLabel): void {
    const hole = this.holes.get(node);
    const l = this.layout;
    if (!hole || !l || !label.text) return;
    const s = l.scale;
    const k = 1 / Math.max(1e-6, s);
    const tag = el('div', 'mado-cue-l');
    tag.textContent = label.text;
    tag.style.cssText =
      `font-size:${12.5 * k}px;padding:${5 * k}px ${9 * k}px;max-width:${340 * k}px;` +
      `border-radius:calc(var(--mado-r-tip) * ${0.7 * k});border-width:calc(var(--mado-edge-w) * ${k});` +
      `box-shadow:0 ${3 * k}px ${10 * k}px rgba(0,0,0,.18);`;
    node.appendChild(tag);
    // Its size on screen: layout px (untransformed) at the zoom it is shown at.
    const size = { w: tag.offsetWidth * s, h: tag.offsetHeight * s };
    const a = this.asset;
    const pic = { x: l.tx, y: l.ty, w: a.w * s, h: a.h * s };
    const x0 = Math.max(pic.x, label.area.x);
    const y0 = Math.max(pic.y, label.area.y);
    const x1 = Math.min(pic.x + pic.w, label.area.x + label.area.w);
    const y1 = Math.min(pic.y + pic.h, label.area.y + label.area.h);
    const area = {
      x: x0 + 8,
      y: y0 + 8,
      w: Math.max(0, x1 - x0 - 16),
      h: Math.max(0, y1 - y0 - 16),
    };
    const ring = { x: l.tx + hole.x * s, y: l.ty + hole.y * s, w: hole.w * s, h: hole.h * s };
    const p = placeLabel(ring, size, area, label.avoid);
    tag.style.left = `${(p.x - l.tx) / s}px`;
    tag.style.top = `${(p.y - l.ty) / s}px`;
  }

  /** Fade a cue out and remove it. */
  hideCue(node: HTMLElement): void {
    if (this.cueNode === node) this.cueNode = null;
    node.classList.remove('mado-cue--in');
    window.setTimeout(() => node.remove(), dur(CUE_FADE_MS) + 40);
  }

  /** Remove every cue at once (a skip, a new step). */
  clearCues(): void {
    this.cueNode = null;
    this.stage.querySelectorAll('.mado-cue').forEach((n) => n.remove());
  }

  /** Fade every cue still lit out (the step is being left, or the end card comes). */
  fadeCues(): void {
    this.stage.querySelectorAll<HTMLElement>('.mado-cue').forEach((n) => this.hideCue(n));
  }

  /** The viewport this stage was last laid out for (`relayout`), to tell when it is stale. */
  get laidOutFor(): { w: number; h: number } {
    return { w: this.viewport.w, h: this.viewport.h };
  }

  /** Framed in a phone story's picture (not the whole viewport). */
  get framed(): boolean {
    return !!this.picture;
  }

  /** Phone story, or a desktop guided step: the beats it walks through, the last where it rests. */
  get beats(): readonly StoryBeat[] | null {
    return this.beatList;
  }

  /** The framing this step rests at under the current viewport. */
  targetLayout(): Layout {
    return this.computeLayout();
  }

  /**
   * Move the camera to `layout`: at once, or over `ms` with a delay and an
   * easing. Sets `currentLayout` immediately so the overlay is drawn where the
   * picture will come to rest; while it travels the overlay follows it
   * (overlays.ts `follow`), so the targets are interactive, where they are
   * seen, before the settle. A scroll step ignores it — its framing is its
   * progress — except for a phone story's walk in.
   */
  setCamera(layout: Layout, move: CameraMove | null = null): void {
    if (this.isScroll && !this.picture) return;
    this.layout = layout;
    this.stage.style.transition = cameraTransition(move);
    this.stage.style.transform = `translate3d(${layout.tx}px, ${layout.ty}px, 0) scale(${layout.scale})`;
    this.syncRail();
  }

  /**
   * Name the layout the camera is heading to before the move starts
   * (`setCamera` with the move follows): `currentLayout` — what the overlay
   * is drawn at — is the destination, while the picture is still where it
   * was put. A scroll step ignores it outside a phone story.
   */
  aim(layout: Layout): void {
    if (!this.isScroll || this.picture) this.layout = layout;
  }

  /** The step's `<img>` has been decoded (or `ms` has passed): paint it without a blank frame. */
  whenDecoded(ms: number, then: () => void): void {
    const img = this.stage.querySelector('img');
    if (!img || ms <= 0 || typeof img.decode !== 'function') return then();
    let done = false;
    const go = (): void => {
      if (done) return;
      done = true;
      clearTimeout(t);
      then();
    };
    const t = setTimeout(go, ms);
    img.decode().then(go, go);
  }

  /**
   * The framing on screen right now, mid-move included — read back from the
   * computed transform so a step change during a camera move hands over from
   * where the picture actually is rather than where it was going. Falls back
   * to the target when there is nothing to read (no layout engine, no move).
   */
  liveLayout(): Layout {
    const target = this.layout!;
    if ((this.isScroll && !this.picture) || typeof getComputedStyle !== 'function') return target;
    const m = /matrix\(([^)]+)\)/.exec(getComputedStyle(this.stage).transform);
    if (!m) return target;
    const v = m[1]!.split(',').map(Number);
    if (v.length !== 6 || v.some((n) => !Number.isFinite(n))) return target;
    return { ...target, scale: v[0]!, tx: v[4]!, ty: v[5]! };
  }

  /** The widest this step alone draws its picture (sizing.ts), when the runtime gave no `sizes`. */
  private ownSizes(): number {
    const layouts = stepLayouts(this.step, this.asset, {
      viewport: this.viewport,
      allowFocus: this.allowFocus,
      motion: this.motion,
      pictureFor: () => this.picture,
      frame: this.frame,
      ...(this.zoom ? { zoom: this.zoom } : {}),
    });
    return layouts.reduce((w, l) => Math.max(w, drawnWidth(this.asset, l, this.viewport)), 0);
  }

  /**
   * A resize can make the picture wider than it was sized for (into focus
   * mode, a larger embed): grow `sizes`, bands included, so they keep the
   * same file. Never shrink it — the larger file is already here, and a
   * smaller `sizes` could only cost a second download. The browser keeps
   * the current picture painted until the larger one has loaded.
   */
  private applySizes(sizes: number): void {
    if (sizes <= this.sizes) return;
    this.sizes = sizes;
    const px = `${Math.round(sizes)}px`;
    this.layer.querySelectorAll('source, img').forEach((n) => {
      const node = n as HTMLImageElement | HTMLSourceElement;
      if (node.sizes) node.sizes = px;
    });
  }

  /**
   * A scroll step's framing at the current progress: the viewport's width,
   * or the legibility zoom on a wide capture shown on a phone, with the
   * vertical offset driven by progress rather than by a focus region. The
   * rule lives in `@mado/schema/camera` `scrollLayout`, shared with the MP4
   * export.
   */
  private scrollAt(): Layout {
    const story = this.storyInput();
    if (story) return scrollStoryLayout(story, this.scrollP, this.panPx);
    return scrollLayout(
      {
        viewport: this.viewport,
        asset: this.asset,
        focus: this.step.focus,
        allowFocus: this.allowFocus,
      },
      this.step.scroll!,
      this.scrollP,
    );
  }

  private applyTransform(animate: boolean): void {
    const l = this.layout!;
    this.stage.style.transition = animate ? `transform ${dur(420)}ms ${EASE}` : 'none';
    this.stage.style.transform = `translate3d(${l.tx}px, ${l.ty}px, 0) scale(${l.scale})`;
  }

  private applySticky(): void {
    const s = this.step.scroll;
    if (!s || !this.layout) return;
    // A scroll story walks the content between the bands, and draws neither.
    for (const band of [this.stickyTop, this.stickyBottom]) {
      if (band) band.style.display = this.picture ? 'none' : '';
    }
    if (this.picture) return;
    const { scale, tx } = this.layout;

    // The bands follow the stage sideways (`tx` is non-zero once the
    // legibility zoom makes the asset wider than the viewport), so the nav
    // bar stays over the columns it belongs to.
    if (this.stickyTop) {
      const h = s.sticky.top * this.asset.h * scale;
      this.stickyTop.style.height = `${h}px`;
      const inner = this.stickyTop.querySelector('img')!;
      inner.style.transformOrigin = '0 0';
      inner.style.transform = `translate3d(${tx}px, 0, 0) scale(${scale})`;
    }
    if (this.stickyBottom) {
      const h = s.sticky.bottom * this.asset.h * scale;
      this.stickyBottom.style.height = `${h}px`;
      const inner = this.stickyBottom.querySelector('img')!;
      inner.style.transformOrigin = '0 0';
      // Pull the bottom of the tall asset into view inside the clipped band.
      inner.style.transform = `translate3d(${tx}px, ${-(this.asset.h - s.sticky.bottom * this.asset.h) * scale}px, 0) scale(${scale})`;
    }
  }

  /** 0..1 through this step's scroll range. Drives the ty offset directly. */
  setScrollProgress(p: number): void {
    if (!this.isScroll) return;
    this.scrollP = Math.max(0, Math.min(1, p));
    this.layout = this.scrollAt();
    this.applyTransform(false);
    this.syncRail();
  }

  get scrollProgress(): number {
    return this.scrollP;
  }

  /** Scrollable distance in CSS px at the current framing (never 0: progress divides by it). */
  scrollRange(): number {
    const story = this.storyInput();
    if (story) return Math.max(1, scrollStoryRange(story));
    return Math.max(1, this.asset.h * this.layout!.scale - this.viewport.h);
  }

  /** Phone story, scroll step: the page is wider than the picture, so it pans sideways. */
  get pans(): boolean {
    // Judged at the column, not wherever a walk-in has the camera now.
    return !!this.storyInput() && this.asset.w * this.scrollAt().scale > this.picture!.w + 0.5;
  }

  get pan(): number {
    return this.panPx;
  }

  /** Phone story, scroll step: pan sideways to `px` from the column (clamped to the page). */
  setPan(px: number): void {
    const story = this.storyInput();
    if (!story) return;
    this.panPx = px;
    this.layout = this.scrollAt();
    // Keep the pan the page could actually take, so a drag back starts at once.
    this.panPx = this.layout.tx - scrollStoryLayout(story, this.scrollP, 0).tx;
    this.applyTransform(false);
  }

  /** What a phone-story scroll step is framed from; null when no story is told for it. */
  private storyInput(): ScrollStoryInput | null {
    const scroll = this.step.scroll;
    if (!scroll || !this.picture) return null;
    return { picture: this.picture, asset: this.asset, scroll, focus: this.step.focus };
  }

  /** Current geometry — the overlay layer needs it to place hotspots. */
  get currentLayout(): Layout {
    return this.layout!;
  }

  /** Animate to a new focus framing within the same step (annotation reveal, etc.). */
  panTo(layout: Layout): void {
    this.layout = layout;
    this.applyTransform(true);
  }

  destroy(): void {
    this.layer.remove();
    // Let go of the decoded picture now, not at the next GC: iOS Safari
    // counts a detached <img>'s bitmap against the tab until then. A step
    // drawing the same file keeps it through its own <img>.
    this.layer.querySelectorAll('source').forEach((n) => n.remove());
    this.layer.querySelectorAll('img').forEach((n) => {
      n.removeAttribute('srcset');
      n.removeAttribute('src');
    });
  }
}

/** A camera move: over `ms`, after `delay`, with an easing. */
export interface CameraMove {
  ms: number;
  delay?: number;
  ease?: string;
}

/**
 * The CSS transition a camera move runs with. One function, used by the stage
 * and by the overlay that follows it (overlays.ts `follow`), so the targets
 * travel on exactly the same curve and clock as the picture.
 */
export function cameraTransition(move: CameraMove | null): string {
  const ms = move ? dur(move.ms) : 0;
  return ms > 0 ? `transform ${ms}ms ${move!.ease ?? EASE} ${dur(move!.delay ?? 0)}ms` : 'none';
}

export function el(tag: string, cls?: string): HTMLElement {
  const n = document.createElement(tag);
  if (cls) n.className = cls;
  return n;
}

/** How much of the doc is a native phone capture — decides `allowFocus`. */
export function isPhoneSource(doc: DemoDoc): boolean {
  return doc.source.kind === 'mobile' && doc.source.captureH > doc.source.captureW;
}
